import { Button } from '@/components/Buttons';
import ProductEligibilityResult from '@/components/products/ProductEligibilityResult';
import useEligibilityBlocked from '@/components/products/useEligibilityBlocked';

import { awaitPaymentCopy, interpolate } from '../copy';
import useChosenProduct from '../hooks/useChosenProduct';

interface AwaitPaymentStepProps {
  /** True once the entitlement poll has used its whole budget without converging. */
  unconfirmed: boolean;
  /** True between opening the tab and the popup reporting back. */
  waiting: boolean;
  /**
   * True when a product was chosen before there was an account to bill, so checkout has not begun.
   * The visitor starts it themselves — the click is what lets us open a tab at all.
   */
  needsStart: boolean;
  onStart: () => void;
  onRetry: () => void;
  /** Send them back to the product list, because this product is not one they may buy. */
  onChooseAnother: () => void;
}

/**
 * What the visitor sees while checkout happens in another tab.
 *
 * Three states, and the distinction between the last two is the point of ADR 0003: waiting is
 * normal, and "still finalising" means the card was charged but our own record has not caught up.
 * Neither is an error, and neither may be worded as one — telling someone who has just paid that
 * something failed is the worst outcome this flow can produce.
 */
const AwaitPaymentStep = ({
  unconfirmed,
  waiting,
  needsStart,
  onStart,
  onRetry,
  onChooseAnother,
}: AwaitPaymentStepProps) => {
  // The start state names the pass they picked, so this step interpolates like the completion card.
  const { product, habitat } = useChosenProduct();
  const values = { productName: product?.name, species: habitat?.title };

  /**
   * The API has already said this visitor may not buy this one — they hold it, or the offer on it
   * is spent. Asked before offering the button rather than after: the same answer comes back from
   * the checkout request, but by then they have pressed a button and been moved somewhere with
   * nothing to explain it.
   *
   * Only where a start is actually being offered. Once checkout is under way the question is
   * settled, and a stale `false` must not interrupt it.
   */
  const eligibilityBlocked = useEligibilityBlocked(product);
  const refused = needsStart && eligibilityBlocked;

  const copy = refused
    ? awaitPaymentCopy.refused
    : needsStart
    ? awaitPaymentCopy.start
    : unconfirmed
    ? awaitPaymentCopy.unconfirmed
    : waiting
    ? awaitPaymentCopy.waiting
    : awaitPaymentCopy.redirecting;

  return (
    <div className="flex flex-col items-center gap-3 p-6 text-center" data-component="speedy-step" data-step="await-payment">
      <h2 className="preflight preflight-h2 text-2xl/7 font-bold text-[#162B1B]">{copy.title}</h2>
      <p className="preflight preflight-p text-sm text-grey-3">{interpolate(copy.body, values)}</p>

      {refused && (
        <>
          {/* The server's own words for why, the same ones the product card would show. */}
          <ProductEligibilityResult result={product?.eligibility} />
          <Button size="lg" className="mt-2 w-full" onClick={onChooseAnother}>
            {awaitPaymentCopy.refused.cta}
          </Button>
        </>
      )}

      {needsStart && !refused && (
        <Button size="lg" className="mt-2 w-full" onClick={onStart}>
          {awaitPaymentCopy.start.cta}
        </Button>
      )}

      {unconfirmed && (
        <Button size="lg" className="mt-2 w-full" onClick={onRetry}>
          {awaitPaymentCopy.unconfirmed.cta}
        </Button>
      )}

      {!unconfirmed && !needsStart && !refused && (
        <p className="preflight preflight-p mt-2 text-xs text-grey-3">{awaitPaymentCopy.redirecting.footnote}</p>
      )}
    </div>
  );
};

export default AwaitPaymentStep;
